
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Action } from "vuex-class";
import { Guard, GuardStatus, MobileGuardsRequest } from "@/store/mobile/types";
import { DispatchGuard } from "@/store/dispatch/types";
import { EventDetails } from "@/store/site-monitor/types";
import { GPSService, GeoCodeResponse } from "@/services/gps.service";
import ManualGuardRow from "./dispatch/ManualGuardRow.vue";
import { get } from "lodash";
import "@/scripts/array-operations";
import api, { CompleteTaskPayload } from '@/services/api.service';
import { Task, TaskGroup } from '@/store/tasks/types';
import { EventTaskTypes } from "@/types/EventTaskTypes";

const DispatchStore = namespace("dispatch");
const SiteMonitor = namespace("siteMonitor");
const Mobile = namespace("mobile");
const Tasks = namespace("tasks");

@Component({
	components: {
		"manual-guard-row": ManualGuardRow
	}
})
export default class DispatchMobile extends Vue {
	// Global store
	@Getter("getUserName") operatorFullName: string;
	@Getter("getMapKey") private mapKey: string;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	// Site monitor store
	@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;

	// Mobile
	@Mobile.Action addDispatchRequest: any;
	@Mobile.Action loadMobileGuardsForEvent: (mobileGuardsRequest: MobileGuardsRequest) => Promise<void>;
	@Mobile.Getter("getMobileGuardsForEvent") mobileGuardsForEvent: Guard[];
	@Tasks.Getter("getEventTasks") eventTasks: TaskGroup[];

	// Dispatch
	@DispatchStore.Action updateDispatchStatus: (eventId: number) => Promise<void>;
	@DispatchStore.Action updateEventLocation: (location: string) => Promise<void>;

	@Prop(Number) public eventId: number;

	public searchText: string = "";
	public noLocation: boolean = false;
	public location: string = "";
	public lastCheckedLatLongValue: string = "";
	private dispatchListInterval: any = null;

	private async mounted() {
		await this.updateDispatchStatus(this.eventId);
	}

	private beforeDestroy() {
		clearInterval(this.dispatchListInterval);
	}

	@Watch("eventDetails")
	async onEventDetailsChanged(value: EventDetails, oldValue: EventDetails) {
		// Only do this if eventDetails has recently been populated
		if (!oldValue)
		{
			// Fetch mobile guards, and if guards are dispatched, poll for updates.
			await this.fetchMobileGuards();
			if (this.areGuardsDispatched)
			{
				this.pollForDispatchedGuards();
			}
		}
	}

	public get areGuardsDispatched() {
		var dispatchedGuards = this.mobileGuardsForEvent.filter(guard => {
			return GuardStatus[guard.status] !== "Available" && guard.eventID === this.eventId;
		});

		return dispatchedGuards.length > 0 ? true : false;
	}

	// Returns the list of available guards for this event, filtered using any text in the search box
	public get filteredGuards() {
		let unsortedEventGuards = this.mobileGuardsForEvent.map(guard => {
			return {
				guardID: guard.guardID,
				callsign: guard.callsign,
				status: guard.status,
				active: guard.active,
				claimed: guard.claimed,
				eventID: guard.eventID,
				mobileGuard: guard.userID != null
			};
		});

		/* Dispatch Sort Order
			Arrived - 3
			En Route - 5
			Dispatched - 2
			Available - 1
		*/
		let sortOrder = [3, 5, 2, 1];

		let eventGuards = [];
		sortOrder.forEach(type => {
			eventGuards.push(
				...unsortedEventGuards.filter(guard => {
					return guard.status == type && !guard.mobileGuard;
				})
			);
			eventGuards.push(
				...unsortedEventGuards.filter(guard => {
					return guard.status == type && guard.mobileGuard;
				})
			);
		});

		let filteredEventGuards = eventGuards.filter((value, index, sourceArray) => {
			return sourceArray.map(guard => guard["guardID"]).indexOf(value["guardID"]) === index;
		});

		if (this.searchText == null || this.searchText.trim() == "") {
			return filteredEventGuards;
		} else {
			return filteredEventGuards.filter(x => x.callsign.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1);
		}
	}

	public async loadDispatchGuardsList() {
		this.searchText = "";
		if (this.eventDetails.latLong && this.eventDetails.latLong != "") {
			await this.fetchMobileGuards();
			await this.updateDispatchStatus(this.eventId);

			if (this.mobileGuardsForEvent && this.mobileGuardsForEvent.length > 0) {
				Vue.nextTick(() => {
					// Focus the search input when they open the dispatch menu
					(this.$refs.searchInput as HTMLElement).focus();
				});
			}
		} else {
			this.noLocation = true;
		}
	}

	public async fetchMobileGuards() {
		if (!this.location)
		{
			await this.convertLocationToLatLong();
		}

		this.updateEventLocation(this.location);

		await this.loadMobileGuardsForEvent({
			EventID: this.eventId,
			Location: this.location,
			IncludeGuardsWithMatchingFilter: this.isIncludeGuardsByAlarmQueueFilterEnabled
		});
	}

	private get isIncludeGuardsByAlarmQueueFilterEnabled(): boolean {
		return this.getFeature(["Alarms", "Dispatch", "IncludeGuardsByAlarmQueueFilter"]);
	}

	private pollForDispatchedGuards() {
		clearInterval(this.dispatchListInterval);

		this.dispatchListInterval = setInterval(async () => {
			if (this.areGuardsDispatched) {
				await this.fetchMobileGuards();
			} else {
				clearInterval(this.dispatchListInterval);
			}
		}, 5000);
	}

	public async dispatchGuardHandler(guardID: number) {
		// This will get the updated location from the event (if a used has clicked on an EventRecord with a location)
		await this.convertLocationToLatLong();

		await this.addDispatchRequest({
			preferredGuardId: guardID,
			patrolZoneId: null,
			eventId: this.eventId,
			requester: this.operatorFullName,
			requesterDescription: "Dispatch to area and investigate",
			location: this.location,
			siteTitle: this.eventDetails.siteTitle
		});
		await this.setDispatchActionComplete()
		await this.fetchMobileGuards();

		this.pollForDispatchedGuards();
	}

	public async clearSearch($event) {
		$event.stopPropagation();
		this.searchText = "";
		await this.$nextTick();
		(this.$refs.searchInput as HTMLElement).focus();
	}

	public async convertLocationToLatLong() {
		if (this.eventDetails.latLong && this.eventDetails.latLong != "") {
			this.noLocation = false;
			let eventLocation = this.eventDetails.latLong;

			const isLatLongInCoordinateFormat = /^-?[\d.]+[NS]? -?[\d.]+[WE]?($|,)/.test(eventLocation);
			if (isLatLongInCoordinateFormat) {
				// If the eventLocation is a lat/long format, just save it
				this.location = eventLocation;
			} else {
				// Otherwise, convert it...
				if (this.lastCheckedLatLongValue === eventLocation) {
					if (this.location === "") {
						this.noLocation = true;
						return;
					}
				} else {
					// Update the lastCheckedLatLongValue to this new latLong value
					this.lastCheckedLatLongValue = eventLocation;

					let geoCoder = new GPSService();
					let response = await geoCoder.GeoCodeLookup(eventLocation, this.mapKey);

					if (!response.results || response.results.length == 0) {
						// No results, so this latLong value is not a valid location, set noLocation to true
						// and return - skipping loading guards for this event
						this.noLocation = true;
						return;
					} else {
						// Got results, set latLong to the coordinates associated with this location and continue
						let addressGeometryLocation = response.results[0].geometry.location;
						this.location = `${addressGeometryLocation.lat} ${addressGeometryLocation.lng}`;
					}
				}
			}
		} else {
			this.noLocation = true;
		}
	}

	public isGuardDispatchedToThisEvent(guard: Guard) {
		if (guard.eventID === this.eventId) {
			if (GuardStatus[guard.status] !== "Available" && guard.claimed === true) {
				return true;
			}
		}

		return false;
	}

	private async setDispatchActionComplete() {
		// We have dispatched the guard, lets now see if there is any task groups with any tasks which
		// has dispatch guard action (task). If so complete that action (Task).
		this.eventTasks.forEach(async taskCategory => {
			const task: Task = taskCategory.tasks.find(
				x => x.eventID == this.eventId && x.taskTypeID == EventTaskTypes.Dispatch);

			if (task && !task.completed) {
				const payload: CompleteTaskPayload = {
					EventID: this.eventId,
					EventTaskID: task.eventTaskID,
					TaskComplete: true,
					Result: null,
					taskData: null
				};
				await api.completeTask(payload);
				return;
			}
		});
	}

	public statusText(guard: Guard) {
		if (guard.status === GuardStatus.EnRoute) {
			return "En Route";
		}

		return GuardStatus[guard.status];
	}

	public buttonText(guard: Guard) {
		if (GuardStatus[guard.status] === "Unavailable") {
			return "Unavailable";
		}

		if (this.isGuardDispatchedToThisEvent(guard)) {
			return "Dispatched";
		}

		return "Dispatch";
	}

	public getFormattedCallsign(callsign: string) {
		return callsign.substring(0, 12) + "...";
	}
}
